import { cn } from "@/lib/utils";

// =============================================================================

/** Deterministic pseudo-random generator: same tiles on server and client */
function mulberry32(seed: number) {
  let state = seed;
  return () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

type TilePatternProps = {
  className?: string;
  count?: number;
  seed?: number;
};

/**
 * Faint squares blinking on a grid, as in the background of the design.
 * Pure CSS animation (no JavaScript at runtime).
 */
export function TilePattern({
  className,
  count = 70,
  seed = 7,
}: TilePatternProps) {
  const random = mulberry32(seed);
  const tiles = Array.from({ length: count }, (_, index) => ({
    id: index,
    // 40 columns × 30 rows grid
    left: Math.floor(random() * 40) * 2.5,
    top: Math.floor(random() * 30) * (100 / 30),
    delay: (random() * 8).toFixed(2),
    duration: (4 + random() * 6).toFixed(2),
  }));

  return (
    <div
      aria-hidden
      className={cn("pointer-events-none overflow-hidden", className)}
    >
      {tiles.map((tile) => (
        <span
          key={tile.id}
          className="absolute aspect-square w-[1.7%] animate-tile rounded-[0.125rem] bg-foreground/[0.035]"
          style={{
            left: `${tile.left}%`,
            top: `${tile.top}%`,
            animationDelay: `${tile.delay}s`,
            animationDuration: `${tile.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

// =============================================================================
